<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <v-icon class="header-icon" size="32">
            {{ isGroupChat ? 'mdi-account-group' : 'mdi-message-text' }}
          </v-icon>
          <h3 class="header-title">{{ isGroupChat ? 'Create Group Chat' : 'Create New Chat' }}</h3>
        </div>
        <button @click="$emit('close')" class="btn-close">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <!-- Chat Type Toggle -->
        <div class="chat-type-toggle">
          <button 
            @click="isGroupChat = false"
            :class="{ active: !isGroupChat }"
            class="toggle-btn"
          >
            <v-icon left size="18">mdi-message-text-outline</v-icon>
            <span>Direct Chat</span>
          </button>
          <button 
            @click="isGroupChat = true"
            :class="{ active: isGroupChat }"
            class="toggle-btn"
          >
            <v-icon left size="18">mdi-account-group</v-icon>
            <span>Group Chat</span>
          </button>
        </div>

        <!-- Group Name (only for groups) -->
        <div v-if="isGroupChat" class="form-section">
          <label class="form-label">
            <v-icon left size="18">mdi-pencil</v-icon>
            Group Name
          </label>
          <div class="input-wrapper">
            <input 
              v-model="groupName"
              placeholder="Enter group name..."
              class="form-input"
              maxlength="50"
            />
            <div class="char-count">{{ groupName.length }}/50</div>
          </div>
        </div>

        <!-- User Search -->
        <div class="form-section">
          <label class="form-label">
            <v-icon left size="18">mdi-account-search</v-icon>
            {{ isGroupChat ? 'Add Members' : 'Select User' }}
          </label>
          <div class="search-wrapper">
            <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
            <input 
              v-model="searchQuery"
              @input="searchUsers"
              placeholder="Search users by username or email..."
              class="search-input"
            />
            <div v-if="searching" class="search-loading">
              <div class="spinner-small"></div>
            </div>
          </div>
        </div>

        <!-- Search Results -->
        <div v-if="searchResults.length > 0" class="search-results">
          <div class="results-header">
            <v-icon left size="16">mdi-account-multiple</v-icon>
            <span>Search Results ({{ searchResults.length }})</span>
          </div>
          <div class="user-list">
            <div 
              v-for="user in searchResults"
              :key="user._id"
              @click="selectUser(user)"
              class="user-item"
              :class="{ selected: isUserSelected(user._id) }"
            >
              <div class="user-avatar">
                <span class="avatar-text">{{ getUserInitials(user) }}</span>
              </div>
              <div class="user-info">
                <div class="user-name">{{ user.verse_username || 'Unknown' }}</div>
              </div>
              <div class="user-status">
                <span :class="{ online: user.isOnline }" class="status-dot"></span>
                <span class="status-text">{{ user.isOnline ? 'Online' : 'Offline' }}</span>
              </div>
              <v-icon 
                v-if="isUserSelected(user._id)" 
                size="20" 
                color="success"
                class="check-icon"
              >
                mdi-check-circle
              </v-icon>
            </div>
          </div>
        </div>

        <!-- Empty Search State -->
        <div v-if="searchQuery.length >= 3 && !searching && searchResults.length === 0" class="empty-search">
          <v-icon size="48" color="grey">mdi-account-search-outline</v-icon>
          <p class="empty-text">No users found</p>
          <p class="empty-hint">Try a different search term</p>
        </div>

        <!-- Search Hint -->
        <div v-if="searchQuery.length < 3 && searchQuery.length > 0" class="search-hint">
          <v-icon left size="16" color="warning">mdi-information</v-icon>
          <span>Type at least 3 characters to search</span>
        </div>

        <!-- Selected Users -->
        <div v-if="selectedUsers.length > 0" class="selected-users">
          <div class="selected-header">
            <div class="selected-title">
              <v-icon left size="18">mdi-account-check</v-icon>
              <span>Selected {{ isGroupChat ? 'Members' : 'User' }} ({{ selectedUsers.length }})</span>
            </div>
            <button @click="clearSelection" class="btn-clear">
              <v-icon left size="14">mdi-close-circle</v-icon>
              Clear All
            </button>
          </div>
          <div class="selected-list">
            <div 
              v-for="user in selectedUsers"
              :key="user._id"
              class="selected-chip"
            >
              <div class="chip-avatar">
                {{ getUserInitials(user) }}
              </div>
              <span class="chip-name">{{ user.verse_username || 'Unknown' }}</span>
              <button @click="removeUser(user._id)" class="chip-remove">
                <v-icon size="14">mdi-close</v-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="error" class="error-message">
          <v-icon left size="20" color="error">mdi-alert-circle</v-icon>
          <span>{{ error }}</span>
        </div>

        <!-- Chat Preview -->
        <div v-if="selectedUsers.length > 0" class="chat-preview">
          <h4 class="preview-title">
            <v-icon left size="20">mdi-eye-outline</v-icon>
            Chat Preview
          </h4>
          <div class="preview-info">
            <div class="preview-item">
              <v-icon left size="16" color="primary">mdi-chat</v-icon>
              <strong>Type:</strong>
              <span>{{ isGroupChat ? 'Group Chat' : 'Direct Chat' }}</span>
            </div>
            <div v-if="isGroupChat && groupName" class="preview-item">
              <v-icon left size="16" color="primary">mdi-label</v-icon>
              <strong>Name:</strong>
              <span>{{ groupName }}</span>
            </div>
            <div class="preview-item">
              <v-icon left size="16" color="primary">mdi-account-multiple</v-icon>
              <strong>Members:</strong>
              <span>{{ selectedUsers.length + 1 }} (including you)</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <v-btn
          @click="$emit('close')"
          variant="outlined"
          prepend-icon="mdi-close"
          class="footer-btn"
        >
          Cancel
        </v-btn>

        <v-btn
          @click="createChat"
          :disabled="!canCreateChat || creating"
          :loading="creating"
          variant="outlined"
          :prepend-icon="creating ? '' : 'mdi-plus'"
          color="success"
          class="footer-btn"
        >
          {{ creating ? 'Creating...' : 'Create Chat' }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import chatService from '@/services/chat/ChatService'

export default {
  name: 'CreateChatModal',
  emits: ['close', 'chat-created'],
  data() {
    return {
      isGroupChat: false,
      groupName: '',
      searchQuery: '',
      searchResults: [],
      selectedUsers: [],
      searching: false,
      creating: false,
      error: null,
      searchTimeout: null
    }
  },
  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  },
  computed: {
    getCurrentUserId() {
      return this.$store.getters.getUserData.id || 'current-user-id'
    },
    canCreateChat() {
      if (this.selectedUsers.length === 0) return false
      if (!this.isGroupChat && this.selectedUsers.length > 1) return false
      if (this.isGroupChat && this.selectedUsers.length < 1) return false
      return true
    }
  },

  methods: {
    closeModal() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }
      this.$emit('close')
    },

    async searchUsers() {
      if (this.searchQuery.length < 3) {
        this.searchResults = []
        return
      }

      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(async () => {
        try {
          this.searching = true
          this.error = null

          chatService.usersSearch(this.searchQuery.trim())
            .then(response => {
              this.searchResults = response.filter(user =>
                !this.isUserSelected(user._id) && user._id !== this.getCurrentUserId
              )
            })
            .catch(error => {
              this.error = 'Failed to search users'
              this.searchResults = []
           })

        } catch (error) {
          this.error = 'Failed to search users'
          this.searchResults = []
        } finally {
          this.searching = false
        }
      }, 300)
    },

    selectUser(user) {
      if (this.isUserSelected(user._id)) {
        this.removeUser(user._id)
        return
      }

      if (!this.isGroupChat && this.selectedUsers.length >= 1) {
        this.selectedUsers = [user]
      } else {
        this.selectedUsers.push(user)
      }

      this.searchResults = this.searchResults.filter(u => u._id !== user._id)
    },

    removeUser(userId) {
      this.selectedUsers = this.selectedUsers.filter(u => u._id !== userId)
      
      const user = this.selectedUsers.find(u => u._id === userId)
      if (user && this.matchesSearch(user)) {
        this.searchResults.push(user)
      }
    },

    isUserSelected(userId) {
      return this.selectedUsers.some(u => u._id === userId)
    },

    clearSelection() {
      this.selectedUsers = []
      if (this.searchQuery.length >= 3) {
        this.searchUsers()
      }
    },

    matchesSearch(user) {
      const query = this.searchQuery.toLowerCase()
      return user.username.toLowerCase().includes(query) ||
             user.verse_username.toLowerCase().includes(query)
    },

    getUserInitials(user) {
      const name = user.verse_username || 'Unknown'
      return name.substring(0, 2).toUpperCase()
    },

    async createChat() {
      if (!this.canCreateChat || this.creating) return

      try {
        this.creating = true
        this.error = null

        const recipients = this.selectedUsers.map(u => u._id)
        
        const chatData = await chatService.createChat(
          recipients,
          this.isGroupChat ? this.groupName : null
        )

        this.$emit('chat-created', chatData)

      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to create chat'
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

/* Modal Content */
.modal-content {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

/* Modal Header */
.modal-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  color: white !important;
}

.header-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.btn-close {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

/* Chat Type Toggle */
.chat-type-toggle {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.toggle-btn {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.toggle-btn.active {
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-color: rgba(0, 123, 255, 0.5);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

/* Form Section */
.form-section {
  margin-bottom: 24px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  outline: none;
  transition: all 0.3s ease;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.form-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.char-count {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

/* Search Wrapper */
.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.search-loading {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Search Results */
.search-results {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 20px;
  animation: slideIn 0.3s ease;
}

.results-header {
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-list {
  max-height: 250px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.user-list::-webkit-scrollbar {
  width: 6px;
}

.user-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

/* User Item */
.user-item {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
}

.user-item:hover {
  background: rgba(0, 123, 255, 0.15);
}

.user-item.selected {
  background: rgba(0, 123, 255, 0.25);
  border-left: 3px solid #007bff;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.avatar-text {
  color: white;
  font-size: 14px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-status {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  animation: pulse 2s ease-in-out infinite;
}

.status-dot.online {
  background: #28a745;
}

.status-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-family: "Darker Grotesque", sans-serif;
}

.check-icon {
  flex-shrink: 0;
}

/* Empty States */
.empty-search,
.search-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  margin-bottom: 20px;
}

.empty-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin: 12px 0 4px 0;
}

.empty-hint {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.search-hint {
  flex-direction: row;
  padding: 12px 16px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

/* Selected Users */
.selected-users {
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 20px;
  animation: slideIn 0.3s ease;
}

.selected-header {
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(40, 167, 69, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.btn-clear {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-clear:hover {
  background: rgba(220, 53, 69, 0.2);
  border-color: #dc3545;
}

.selected-list {
  padding: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 6px 12px;
  transition: all 0.2s ease;
}

.selected-chip:hover {
  background: rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.4);
}

.chip-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
}

.chip-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: white;
}

.chip-remove {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.chip-remove:hover {
  background: rgba(220, 53, 69, 0.3);
  color: #dc3545;
}

/* Error Message */
.error-message {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.4);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: #dc3545;
  animation: shake 0.3s ease;
}

/* Chat Preview */
.chat-preview {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.preview-title {
  margin: 0 0 16px 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);

  strong {
    font-weight: 600;
    color: white;
    margin-right: 6px;
  }

  span {
    color: rgba(255, 255, 255, 0.7);
  }
}

/* Modal Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(0, 0, 0, 0.3);
}

.footer-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  min-width: 120px !important;
}

.footer-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  .modal-header {
    padding: 20px;
  }

  .header-title {
    font-size: 20px;
  }

  .modal-body {
    padding: 16px 20px;
  }

  .chat-type-toggle {
    flex-direction: column;
    gap: 8px;
  }

  .selected-list {
    padding: 12px;
  }

  .modal-footer {
    padding: 16px 20px;
    flex-direction: column-reverse;

    .footer-btn {
      width: 100%;
    }
  }
}

/* Vuetify Overrides */
:deep(.v-btn:hover) > .v-btn__overlay {
  opacity: 0.1;
}
</style>